/**
 * Qawaem Agent — full-screen workspace (dedicated page per ERP module)
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Brain,
  Trash2,
  Inbox,
  FileText,
  Calendar,
  Clock,
  ArrowRightLeft,
  Package,
  ShoppingCart,
  PackageCheck,
  TrendingUp,
  Truck,
  Users,
  CalendarDays,
  Banknote,
  FileCheck,
  AlertTriangle,
  Siren,
  BookOpen,
  Receipt,
  Landmark,
  FileSpreadsheet,
  Scale,
  Wallet,
  PiggyBank,
  BarChart3,
  Handshake,
  ListTodo,
  RefreshCw,
  Factory,
  ShieldCheck,
  Wrench,
  XCircle,
  ClipboardCheck,
  Globe,
  Ship,
  Car,
  Droplet,
  Route,
  Store,
  Megaphone,
  UserPlus,
  GitBranch,
  Headphones,
  Columns,
  Workflow,
  CheckSquare,
} from 'lucide-react';
import { useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAIAgent, useAIAgentChat } from '../contexts/AIAgentContext';
import aiAgentService from '../services/aiAgentService';
import { resolveAgentModuleFromAgentPath } from '../config/modules';
import { getWorkspaceTabsForModule } from '../config/agentWorkspaceTabs';
import AIAgentMessageThread from './AIAgentMessageThread';
import AIAgentComposer from './AIAgentComposer';
import AIAgentDocumentFormModal from './AIAgentDocumentFormModal';
import useAgentInbox from '../hooks/useAgentInbox';
import useAgentSidePanels from '../hooks/useAgentSidePanels';

export default function AIAgentWorkspacePage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.workspacePage.${key}`, opts);
  const location = useLocation();
  const agentDef = resolveAgentModuleFromAgentPath(location.pathname);
  const { setActiveModuleId } = useAIAgent();
  const chat = useAIAgentChat();
  const {
    isReady,
    licenseBlocked,
    entries,
    inputMessage,
    setInputMessage,
    isProcessing,
    pendingConfirmation,
    pendingPlan,
    isBuildingPlan,
    inputRef,
    isVoiceSupported,
    isListening,
    isTranscribing,
    recordingMs,
    audioLevels,
    voiceError,
    isVoiceSession,
    handleSend,
    handleConfirm,
    handleCancel,
    handleBuildPlan,
    handleCancelPlan,
    handleClearHistory,
    onInputKeyDown,
    handleVoiceStart,
    handleVoiceStop,
    handleVoiceCancel,
    agentMode,
    setAgentMode,
    pendingAttachment,
    attachmentError,
    handleAttachmentSelect,
    handleAttachmentClear,
    handleAttachmentDrop,
    activeModule,
    recentSessions,
    activeSessionId,
    handleOpenSession,
    handleToggleSessionShare,
    handleDeleteSession,
    handleOpenDocumentHandoff,
    activeDocumentDraft,
    isDocumentFormModalOpen,
    handleCloseDocumentFormModal,
    handleOpenDocumentEditor,
    handleDraftQuickAction,
    handleDocumentSaved,
  } = chat;

  const { inboxItems, isInboxLoading } = useAgentInbox(agentDef?.id);
  const {
    panels,
    counts,
    recentDocuments,
    paymentGroups,
    paymentCount,
    isPanelsLoading,
  } = useAgentSidePanels(agentDef?.id);
  const [sidePanel, setSidePanel] = useState(null);

  const workspaceTabs = activeModule?.workspaceTabs || getWorkspaceTabsForModule(agentDef?.id);

  const TAB_ICONS = useMemo(
    () => ({
      Inbox,
      FileText,
      Calendar,
      Clock,
      ArrowRightLeft,
      Package,
      ShoppingCart,
      PackageCheck,
      TrendingUp,
      Truck,
      Users,
      CalendarDays,
      Banknote,
      FileCheck,
      AlertTriangle,
      Siren,
      BookOpen,
      Receipt,
      Landmark,
      FileSpreadsheet,
      Scale,
      Wallet,
      PiggyBank,
      BarChart3,
      Handshake,
      ListTodo,
      RefreshCw,
      Factory,
      ShieldCheck,
      Wrench,
      XCircle,
      ClipboardCheck,
      Globe,
      Ship,
      Car,
      Droplet,
      Route,
      Store,
      Megaphone,
      UserPlus,
      GitBranch,
      Headphones,
      Columns,
      Workflow,
      CheckSquare,
    }),
    [],
  );

  const getTabCount = (tab) => {
    if (tab.id === 'inbox') return inboxItems.length;
    if (tab.id === 'sessions') return recentSessions.length;
    if (tab.countKey && counts[tab.countKey] != null) return counts[tab.countKey];
    if (tab.id === 'documents') return recentDocuments.length;
    if (tab.id === 'payments') return paymentCount;
    const panel = panels[tab.id];
    return panel?.count ?? panel?.items?.length ?? 0;
  };

  const toggleSidePanel = (panel) => {
    setSidePanel((current) => (current === panel ? null : panel));
  };

  const panelButtonClass = (panel) =>
    `px-3 sm:px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center gap-2 border ${
      sidePanel === panel
        ? 'bg-blue-500/25 border-blue-400/40 text-white shadow-lg shadow-blue-500/20'
        : 'bg-white/5 border-white/20 text-white/80 hover:bg-white/10 hover:text-white'
    }`;

  useEffect(() => {
    if (agentDef?.id) {
      setActiveModuleId(agentDef.id);
    }
  }, [agentDef?.id, setActiveModuleId]);

  useEffect(() => {
    if (isReady) {
      aiAgentService.syncVectorIndexInBackground().catch(() => {});
    }
  }, [isReady]);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 120);
  }, [inputRef, agentDef?.id]);

  if (!agentDef) {
    return <Navigate to="/facturation/agent" replace />;
  }

  const handleChipClick = (prompt) => {
    setInputMessage(prompt);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleInboxTreat = (item) => {
    if (!item?.prompt || isProcessing) return;
    handleSend(item.prompt);
  };

  const handlePanelTreat = (item) => {
    if (!item?.prompt || isProcessing) return;
    handleSend(item.prompt);
  };

  const panelCountBadge = (count) =>
    count > 0 ? (
      <span className="ml-0.5 inline-flex min-w-[1.15rem] h-[1.15rem] items-center justify-center rounded-full bg-blue-500/30 text-[0.65rem] font-bold text-blue-100 px-1">
        {count > 99 ? '99+' : count}
      </span>
    ) : null;

  const emptyStateActions =
    activeModule?.emptyStateActions ||
    (activeModule?.quickChips || []).slice(0, 4).map((chip) => ({
      title: chip.label,
      desc: agentDef.shortLabel,
      prompt: chip.prompt,
    }));

  return (
    <div className="space-y-3 employees-container flex flex-col min-h-[calc(100vh-8rem)] max-md:min-h-[calc(100vh-6rem)]">
      <div className="flex justify-between items-start gap-4 list-toolbar-mobile-layout shrink-0">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Brain className="h-8 w-8 text-blue-400" />
            {agentDef.label}
          </h1>
          <p className="text-white/60 mt-2">{agentDef.subtitle}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {workspaceTabs.map((tab) => {
            const TabIcon = TAB_ICONS[tab.icon] || Inbox;
            const count = getTabCount(tab);

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => toggleSidePanel(tab.id)}
                className={panelButtonClass(tab.id)}
                title={tab.label}
                aria-pressed={sidePanel === tab.id}
              >
                <TabIcon className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">{tab.label}</span>
                {panelCountBadge(count)}
              </button>
            );
          })}
          {entries.length > 0 && (
            <button
              type="button"
              onClick={handleClearHistory}
              className="bg-white/5 border border-white/20 hover:bg-red-500/20 hover:border-red-500/30 text-white px-4 py-2.5 rounded-xl font-semibold hover:text-red-200 transition-all duration-300 flex items-center gap-2"
              title={p('clear_conversation')}
            >
              <Trash2 className="h-5 w-5" />
              <span className="hidden sm:inline">{p('clear')}</span>
            </button>
          )}
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden flex flex-col flex-1 min-h-0">
        <main className="flex flex-col flex-1 min-h-0 overflow-hidden px-4 sm:px-6 pt-4 pb-2">
          <AIAgentMessageThread
              variant="workspace"
              entries={entries}
              pendingConfirmation={pendingConfirmation}
              pendingPlan={pendingPlan}
              isBuildingPlan={isBuildingPlan}
              isProcessing={isProcessing}
              agentMode={agentMode}
              licenseBlocked={licenseBlocked}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
              onBuildPlan={handleBuildPlan}
              onCancelPlan={handleCancelPlan}
              onQuickPrompt={handleChipClick}
              emptyStateActions={emptyStateActions}
              emptyStateHint={p('empty_state_hint', { module: agentDef.shortLabel })}
              recentSessions={recentSessions}
              activeSessionId={activeSessionId}
              onOpenSession={handleOpenSession}
              onToggleSessionShare={handleToggleSessionShare}
              onDeleteSession={handleDeleteSession}
              inboxItems={inboxItems}
              isInboxLoading={isInboxLoading}
              onInboxTreat={handleInboxTreat}
              sidePanel={sidePanel}
              panels={panels}
              recentDocuments={recentDocuments}
              paymentGroups={paymentGroups}
              isPanelsLoading={isPanelsLoading}
              onPanelTreat={handlePanelTreat}
              onDocumentTreat={handlePanelTreat}
              onPaymentTreat={handlePanelTreat}
              onOpenDocumentHandoff={handleOpenDocumentHandoff}
              onOpenDocumentEditor={handleOpenDocumentEditor}
            onDraftQuickAction={handleDraftQuickAction}
          />
        </main>

        <footer className="shrink-0 px-4 sm:px-6 py-4 border-t border-white/10">
          <AIAgentComposer
            variant="workspace"
            inputRef={inputRef}
            inputMessage={inputMessage}
            setInputMessage={setInputMessage}
            isProcessing={isProcessing}
            isReady={isReady}
            isVoiceSupported={isVoiceSupported}
            isVoiceSession={isVoiceSession}
            isListening={isListening}
            isTranscribing={isTranscribing}
            recordingMs={recordingMs}
            audioLevels={audioLevels}
            voiceError={voiceError}
            onInputKeyDown={onInputKeyDown}
            onSend={handleSend}
            onVoiceStart={handleVoiceStart}
            onVoiceStop={handleVoiceStop}
            onVoiceCancel={handleVoiceCancel}
            showShortcut={false}
            placeholder={activeModule?.placeholder || agentDef.placeholder}
            contextCaption={p('context_caption', { module: agentDef.shortLabel })}
            agentMode={agentMode}
            onAgentModeChange={setAgentMode}
            pendingAttachment={pendingAttachment}
            attachmentError={attachmentError}
            onAttachmentSelect={handleAttachmentSelect}
            onAttachmentClear={handleAttachmentClear}
            onAttachmentDrop={handleAttachmentDrop}
          />
        </footer>
      </div>

      <AIAgentDocumentFormModal
        open={isDocumentFormModalOpen}
        draft={activeDocumentDraft}
        onClose={handleCloseDocumentFormModal}
        onDocumentSaved={handleDocumentSaved}
      />
    </div>
  );
}
